import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiUserCheck, FiPhone, FiExternalLink} = FiIcons;

const ClientCoordinatorsWidget = () => {
  const {data} = useAuth0Data();

  // Filter sales team members who are client coordinators
  const clientCoordinators = data.salesTeam.filter(member => 
    member.role === 'client-coordinator'
  );

  const getPerformanceColor = (current, goal) => {
    if (goal === 0) return 'text-gray-600';
    const percentage = (current / goal) * 100;
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (clientCoordinators.length === 0) {
    return (
      <Link to="/data-management/sales-team" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Client Coordinators</h3>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-purple-600" />
            </div>
          </div>
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiUserCheck} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No client coordinators found.</p>
            <p className="text-sm">Click to add client coordinators</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/data-management/sales-team" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">Client Coordinators</h3>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-purple-600" />
          </div>
        </div>

        <div className="space-y-6">
          {clientCoordinators.map((coordinator) => (
            <div key={coordinator.id} className="border border-gray-100 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-4">{coordinator.name}</h4>
              
              {/* QTD Performance */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-700 mb-3">Quarterly Performance</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <SafeIcon icon={FiUserCheck} className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                    <p className={`text-lg font-bold ${getPerformanceColor(
                      coordinator.qtdPlacements || 0,
                      coordinator.qtdPlacementsGoal || 0
                    )}`}>
                      {coordinator.qtdPlacements || 0}/{coordinator.qtdPlacementsGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Placements</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                    <p className={`text-lg font-bold ${getPerformanceColor(
                      coordinator.qtdIntake || 0,
                      coordinator.qtdIntakeGoal || 0
                    )}`}>
                      {coordinator.qtdIntake || 0}/{coordinator.qtdIntakeGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Intake Calls</p>
                  </div>
                </div>
              </div>

              {/* YTD Performance */}
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">Year-to-Date Performance</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <SafeIcon icon={FiUserCheck} className="w-5 h-5 mx-auto mb-1 text-purple-600" />
                    <p className={`text-lg font-bold ${getPerformanceColor(
                      coordinator.ytdPlacements || 0,
                      coordinator.ytdPlacementsGoal || 0
                    )}`}>
                      {coordinator.ytdPlacements || 0}/{coordinator.ytdPlacementsGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Placements</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                    <p className={`text-lg font-bold ${getPerformanceColor(
                      coordinator.ytdIntake || 0,
                      coordinator.ytdIntakeGoal || 0
                    )}`}>
                      {coordinator.ytdIntake || 0}/{coordinator.ytdIntakeGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Intake Calls</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-500">Total YTD Placements</p>
              <p className="text-xl font-bold text-purple-600">
                {clientCoordinators.reduce((sum, coord) => sum + (coord.ytdPlacements || 0), 0)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total YTD Intake</p>
              <p className="text-xl font-bold text-blue-600">
                {clientCoordinators.reduce((sum, coord) => sum + (coord.ytdIntake || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default ClientCoordinatorsWidget;